import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// ============================================================
// Zifek BI — carte graphique réutilisable (barres / ligne / camembert).
// Les couleurs proviennent du système de palette (tokens oklch).
// ============================================================

export interface ChartPoint {
  label: string;
  value: number;
}

const PIE_COLORS = [
  'oklch(var(--primary-500))',
  'oklch(var(--accent-500))',
  'oklch(var(--secondary-500))',
  'oklch(var(--primary-300))',
  'oklch(var(--accent-300))',
  'oklch(var(--secondary-300))',
  'oklch(var(--foreground-500))',
  'oklch(var(--foreground-300))',
];

const axisProps = {
  tick: { fontSize: 11, fill: 'oklch(var(--foreground-500))' },
  tickLine: false,
} as const;

const tooltipStyle = {
  backgroundColor: 'oklch(var(--background-50))',
  border: '1px solid oklch(var(--foreground-200) / 0.4)',
  borderRadius: '8px',
  fontSize: '12px',
  color: 'oklch(var(--foreground-900))',
} as const;

export type ChartType = 'bar' | 'line' | 'pie' | 'column' | 'area';

export default function ChartCard({
  title,
  chartType,
  points,
  height = 260,
  onSelect,
}: {
  title: string;
  chartType: ChartType;
  points: ChartPoint[];
  height?: number;
  onSelect?: (label: string) => void;
}) {
  const handleSelect = (data: unknown) => {
    if (!onSelect) return;
    const d = data as { label?: string; name?: string; payload?: { label?: string } };
    const label = d?.payload?.label ?? d?.label ?? d?.name;
    if (label !== undefined && label !== null && label !== '') onSelect(String(label));
  };

  if (!points || points.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <p className="text-sm font-semibold text-foreground-800 mb-3">{title}</p>
        <p className="text-xs text-foreground-400 py-8 text-center">Aucune donnée exploitable</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
      <p className="text-sm font-semibold text-foreground-800 mb-3 truncate">{title}</p>
      <div style={{ height, cursor: onSelect ? 'pointer' : 'default' }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'pie' ? (
            <PieChart>
              <Pie data={points} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={height / 2 - 30} label={(e: { label?: string }) => e.label || ''} onClick={handleSelect}>
                {points.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          ) : chartType === 'line' || chartType === 'area' ? (
            <LineChart data={points} margin={{ top: 5, right: 12, left: -12, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--foreground-200) / 0.4)" />
              <XAxis dataKey="label" {...axisProps} axisLine={{ stroke: 'oklch(var(--foreground-200) / 0.4)' }} />
              <YAxis {...axisProps} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="oklch(var(--primary-500))"
                strokeWidth={2}
                fill={chartType === 'area' ? 'oklch(var(--primary-500) / 0.18)' : 'transparent'}
                dot={{ r: 3, fill: 'oklch(var(--primary-500))', strokeWidth: 0 }}
              />
            </LineChart>
          ) : (
            <BarChart data={points} margin={{ top: 5, right: 12, left: -12, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--foreground-200) / 0.4)" vertical={false} />
              <XAxis dataKey="label" {...axisProps} axisLine={{ stroke: 'oklch(var(--foreground-200) / 0.4)' }} />
              <YAxis {...axisProps} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'oklch(var(--foreground-200) / 0.2)' }} />
              <Bar
                dataKey="value"
                fill={chartType === 'column' ? 'oklch(var(--accent-500))' : 'oklch(var(--primary-500))'}
                radius={[6, 6, 0, 0]}
                maxBarSize={chartType === 'column' ? 34 : 56}
                onClick={handleSelect}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}